/**
 * Reporting `401 IDENTITY_MISMATCH` back to the auth provider.
 *
 * The auth service refuses a request whose session cookie and `Authorization:
 * Bearer` header name different users, rather than silently answering as one of
 * them. That refusal is only half a fix: a client that treats it like any other
 * 401 and clears its token turns a silent wrong-identity into a dead end the
 * user cannot get out of. The right response is the opposite — the cookie is
 * the session and the stored token is the stale copy, so the provider
 * re-derives from the cookie and the user stays signed in.
 *
 * Every client that sends a Bearer header to the auth service must route its
 * 401s through `handleIdentityMismatch`. See
 * apps/auth/docs/session-credential-standardization-plan.md (ID-4).
 */
/**
 * Dispatched on `window` when a client sees `401 IDENTITY_MISMATCH`.
 * `AppAuthProvider` listens and re-derives the stored token from the cookie.
 */
export const IDENTITY_MISMATCH_EVENT = 'airclou:identity-mismatch';
/** The error code the auth service answers with on a two-identity request. */
export const IDENTITY_MISMATCH_CODE = 'IDENTITY_MISMATCH';
/**
 * Read the error code out of an auth-service error body. Two shapes are in
 * play: the standard `{ error: { code, message } }` envelope, and
 * `requireSensitiveAuth`'s legacy `{ error: <string>, code }`.
 */
export function readErrorCode(body) {
    return body?.error?.code ?? body?.code;
}
/**
 * Ask the provider to re-derive from the session cookie.
 *
 * Returns `true` when the response was an identity mismatch, which is the
 * caller's signal NOT to clear its token: `if (!handleIdentityMismatch(status,
 * body)) clearToken()`.
 */
export function handleIdentityMismatch(status, body) {
    if (status !== 401 || readErrorCode(body) !== IDENTITY_MISMATCH_CODE)
        return false;
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(IDENTITY_MISMATCH_EVENT));
    }
    return true;
}
let reconciler = null;
/**
 * Called by `AppAuthProvider` on mount. Returns the unregister function, so the
 * effect that registers can clean up on unmount.
 */
export function registerIdentityReconciler(fn) {
    reconciler = fn;
    return () => {
        if (reconciler === fn)
            reconciler = null;
    };
}
/**
 * Report a mismatch and wait for the provider to finish re-deriving.
 *
 * Returns `true` when the response was an identity mismatch — the caller's
 * signal to retry with the freshly derived token rather than to clear its own.
 * Falls back to the event when no provider has registered (an app that renders
 * API calls outside the provider), so the recovery still happens; it just cannot
 * be awaited.
 */
export async function reportIdentityMismatch(status, body) {
    if (status !== 401 || readErrorCode(body) !== IDENTITY_MISMATCH_CODE)
        return false;
    if (reconciler) {
        try {
            await reconciler();
        }
        catch {
            // A failed reconcile is not the caller's problem — it retries or not on
            // the token it has, and the ordinary 401 handling still applies.
        }
        return true;
    }
    return handleIdentityMismatch(status, body);
}
